// Port of PROTO news.html: "Public News Paper" — a photographed old paper
// sheet (SVG filter tears/browns the edges), a masthead that scrambles into
// a different joke paper name on click, and the three articles verbatim.
import { useEffect, useRef, useState } from 'react';
import { Opener } from '../Opener.js';
import { reduceMotion, useReveal } from '../motion.js';
import './NewsPage.css';

const MASTHEADS_HREF = '/mastheads.css';
const GOOGLE_FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Leckerli+One&family=Jacques+Francois+Shadow&display=swap';

const PAPERS: Array<[string, string, number]> = [
  ['The Daily Heart Pulse', 'Printed promptly after every victory', 1],
  ['The Toxic Telegraph', 'Blessed by None, Read by All', 2],
  ['Kung Fu What!', 'Мы не знаем, что делаем. Но делаем красиво', 3],
  ['Le Courrier Corsaire', 'Commandé depuis le pont, relu dans le vin', 4],
  ['The Rum Runners Journal', 'Best enjoyed slightly tipsy, truth served by the bottle.', 5],
  ['Verenigde Prutsers Krant', 'Printed promptly after every defeat', 6],
  ['The Daily Bork Bark', 'Barking dogs don’t bite', 7],
  ['Early Gray Gazette', 'Is it time for TEA yet?', 8],
  ['Ahoy! Yer ol’ paper!', 'Good fer readin’, better fer wipin’!', 1],
  ['New World Chaos', 'Many subscribers, but none who can read', 8],
  ['United We Newspaper stand', 'Creating news in every wind direction', 1],
  ['Totally Nonchalant Articles', 'Because only legends read Totally Nonchalant Articles', 4],
];
const GLYPHS = 'ABCDEFGHIKLMNOPRSTUVWXYZ#&$*';

const BIMINI = [
  'As the rum cooled and the sea grew restless, the brave sailors of HEART and AHOY—resupplied generously by IMPRI’s own Eric “Powder Monkey” Bloodhair—rose to meet a most inconvenient British invasion. Clan LDDL had decided that Bimini looked far too peaceful.',
  'What followed was a proper naval clash: sails torn, broadsides booming, and courage tested on all sides.',
  'The battle opened with a decisive strike—[HEART] Hartje landed the first kill by ambushing a stray British ship that had wandered too far from its fleet, likely in search of biscuits.',
  '[AHOY] Meatball Ron held his patch of sea with such ferocity, not even a jellyfish dared pass through. He guarded it like a terrier and thrashed anyone foolish enough to try.',
  'Elsewhere, [AHOY] Capn Rocko soloed a Niagara-class ship, armed with grit, gunpowder, and what we can only assume was an unsettling grin throughout.',
  'New blood made their mark too—[HEART] Mencio notched his first PVP kill, a moment that won’t soon be forgotten.',
  '[HEART] Minori van Eiksteen tallied three kills with the calm efficiency of someone cleaning up after a tavern brawl—swift, thorough, and nothing left afloat.',
  'Though [AHOY] Hussar’s sails were reduced to pirate laundry by the end, he still sank two ships. Turns out, you don’t need speed when your aim’s that deadly.',
  'And then there was [HEART] Sallee Rover, whose ship sank somewhere between a stiff breeze and a bad mood. Unfazed, he clambered aboard a nearby ally’s vessel like it was all part of the plan and carried on without missing a beat.',
  'When the waves settled, 11 British ships had been sunk at the cost of just three of ours. The sea itself seemed saltier afterward—perhaps from British tears.',
];
const ORANJE = [
  'Clan HEART had secretly moved the plunder seized from Dutch ships near Fort Oranje out of the city. With the French Alliance tightening its grip on the harbor, there was no time to waste — the riches had to vanish before the cannons roared.',
  'Strangely enough, they were escorted by a Dutch Lynx warship, its colors fluttering proudly. At its helm stood Captain (NLwolf)_, known across the coast as the Kind Captain — a man too honorable for the world he sailed. He saluted the pirates with a steady hand and a trusting heart, wishing them a safe journey. Little did he realize that beneath their decks, tucked among barrels and crates, lay the stolen silverware of his own bloodline — heirlooms destined never to return.',
];
const BACK = [
  'The seas have been wiped clean, and we’ve hoisted our colors once more! All hands are welcome back on deck since Sunday 13th April — the winds favor the bold. Ping an officer on Discord to claim yer spot aboard!',
  '<strong>Help for Returnin’ Sailors</strong>',
  'The sea’s a harsh mistress, but no sailor stands alone. To help ye set sail swift and sure, we’re offerin’ a 7-4th rank ship or medium cannons (up to 32p) for yer DLC vessels. It’ll save ye at least two weeks of grindin’ and get ye back in the thick of adventure — raiding, trading, and claimin’ new riches. The horizon calls, and we’ll be sailin’ it together!',
];

export function NewsPage() {
  const scopeRef = useRef<HTMLDivElement | null>(null);
  const mastRef = useRef<HTMLHeadingElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const busyRef = useRef(false);
  const curRef = useRef(5);

  const [fontClass, setFontClass] = useState('Font6');
  const [tag, setTag] = useState('Printed promptly after every defeat');

  useReveal(scopeRef);

  // Google Fonts (Leckerli One, Jacques Francois Shadow) + the prototype's
  // mastheads.css only while this page is mounted; both removed on unmount.
  useEffect(() => {
    const fonts = document.createElement('link');
    fonts.rel = 'stylesheet';
    fonts.href = GOOGLE_FONTS_HREF;
    const mastheads = document.createElement('link');
    mastheads.rel = 'stylesheet';
    mastheads.href = MASTHEADS_HREF;
    document.head.appendChild(fonts);
    document.head.appendChild(mastheads);
    return () => {
      fonts.remove();
      mastheads.remove();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  function handleReprint(): void {
    if (busyRef.current) return;
    let next = curRef.current;
    while (next === curRef.current) next = Math.floor(Math.random() * PAPERS.length);
    curRef.current = next;
    const entry = PAPERS[next];
    if (!entry) return;
    const [title, nextTag, font] = entry;
    setFontClass(`Font${font}`);
    setTag(nextTag);
    const el = mastRef.current;
    if (!el) return;
    if (reduceMotion) {
      el.textContent = title;
      return;
    }
    busyRef.current = true;
    let frame = 0;
    const from = el.textContent || '';
    const len = Math.max(from.length, title.length);
    const tick = () => {
      frame += 1;
      let out = '';
      for (let i = 0; i < len; i++) {
        const target = title[i];
        out += frame >= i * 1.2 + 6 ? target || '' : target === ' ' ? ' ' : (GLYPHS[(Math.random() * GLYPHS.length) | 0] ?? '');
      }
      el.textContent = out;
      if (frame < len * 1.2 + 6) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        el.textContent = title;
        busyRef.current = false;
        rafRef.current = null;
      }
    };
    tick();
  }

  return (
    <>
      <Opener lines={['Public', 'News Paper']} />
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true" focusable="false">
        <filter id="old-paper" x="-4%" y="-4%" width="108%" height="108%" colorInterpolationFilters="sRGB">
          <feTurbulence type="fractalNoise" baseFrequency=".018" numOctaves="3" seed="7" result="wav" />
          <feDisplacementMap in="SourceGraphic" in2="wav" scale="13" xChannelSelector="R" yChannelSelector="G" result="d1" />
          <feTurbulence type="fractalNoise" baseFrequency=".35" numOctaves="2" seed="3" result="fib" />
          <feDisplacementMap in="d1" in2="fib" scale="5" xChannelSelector="R" yChannelSelector="G" result="sheet" />
          <feGaussianBlur in="sheet" stdDeviation="14" result="soft" />
          <feFlood floodColor="#6B4520" floodOpacity=".22" />
          <feComposite in2="soft" operator="out" />
          <feComposite in2="sheet" operator="in" result="burn" />
          <feMerge>
            <feMergeNode in="sheet" />
            <feMergeNode in="burn" />
          </feMerge>
        </filter>
      </svg>
      <div ref={scopeRef}>
        <section className="wrap">
          <div className="sheet-wrap">
            <div className="sheet-bg" aria-hidden="true">
              <div className="face" />
              <span className="curl" />
            </div>
            <div className="paper">
              <button type="button" id="reprint" className="reprint" onClick={handleReprint}>
                Where’s the real news?
              </button>
              <div className="mast">
                <h2 ref={mastRef} id="mast" className={fontClass} aria-live="polite">
                  Verenigde Prutsers Krant
                </h2>
                <span className="tag">
                  Circulated Across the Caribbean - <span id="tag">{tag}</span>
                </span>
              </div>
              <div className="mast-row">
                <span>
                  <b>Single Copy</b> $ 1.00
                </span>
                <span>
                  <b>Sunday Edition</b> $ 1.50
                </span>
                <span>
                  <b>By carrier</b> 50 cent <sup>Per week</sup>
                </span>
              </div>
              <div className="grid">
                <article className="lead" data-reveal>
                  <h2>Victory at Bimini – Port Battle Report</h2>
                  <p className="by">Filed by an anonymous fleet officer – 3rd of May 2025.</p>
                  <div className="cols">
                    {BIMINI.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))}
                  </div>
                </article>
                <div className="aside">
                  <article className="side" data-reveal>
                    <h2>Plunder of Fort Oranje has been relocated</h2>
                    <div className="cols">
                      {ORANJE.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                  </article>
                  <article className="side" data-reveal>
                    <h2>Clan Heart is back up on the Caribbean server</h2>
                    <div className="cols">
                      {BACK.map((p, i) => (
                        <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                      ))}
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
